/**
 * Translation utility functions
 * This provides a simple interface for accessing translations throughout the app
 */

import { useContext } from 'react';
import LanguageContext from './LanguageContext';

// Import all translation modules
import commonTranslations from '../translations/common';
import headerTranslations from '../translations/header';
import footerTranslations from '../translations/footer';
import homeTranslations from '../translations/home';
import aboutTranslations from '../translations/about';
import contactTranslations from '../translations/contact';
import serviceTranslations from '../translations/service';
import labTranslations from '../translations/lab';
import principlesTranslations from '../translations/principles';
import privacyTranslations from '../translations/privacy';
import disclaimerTranslations from '../translations/disclaimer';
import accessibilityTranslations from '../translations/accessibility';
import galleryTranslations from '../translations/gallery';
import acrossChaptersMapTranslations from '../translations/acrossChaptersMap';

// All available translation namespaces
const TRANSLATIONS = {
  common: commonTranslations,
  header: headerTranslations,
  footer: footerTranslations,
  home: homeTranslations,
  about: aboutTranslations,
  contact: contactTranslations,
  service: serviceTranslations,
  lab: labTranslations,
  principles: principlesTranslations,
  privacy: privacyTranslations,
  disclaimer: disclaimerTranslations,
  accessibility: accessibilityTranslations,
  gallery: galleryTranslations,
  acrossChaptersMap: acrossChaptersMapTranslations,
};

/**
 * Get translation for a specific key in a namespace
 * @param {string} lang - Language code (e.g., 'en', 'de')
 * @param {string} namespace - Translation namespace (e.g., 'common', 'header')
 * @param {string} key - Translation key
 * @param {any} fallback - Fallback value if translation not found
 * @returns {any} Translated value or fallback
 */
export const getTranslation = (lang, namespace, key, fallback = key) => {
  try {
    const translations = TRANSLATIONS[namespace];
    if (!translations) {
      console.warn(`Translation namespace '${namespace}' not found`);
      return fallback;
    }
    
    const langTranslations = translations[lang];
    if (!langTranslations) {
      console.warn(`Language '${lang}' not found in namespace '${namespace}'`);
      return fallback;
    }
    
    // Handle nested keys (e.g., 'hero.title')
    const keys = key.split('.');
    let value = langTranslations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && value[k] !== undefined) {
        value = value[k];
      } else {
        // Fallback to English if available
        const enTranslations = translations.en;
        if (enTranslations) {
          let enValue = enTranslations;
          for (const ek of keys) {
            if (enValue && typeof enValue === 'object' && enValue[ek] !== undefined) {
              enValue = enValue[ek];
            } else {
              return fallback;
            }
          }
          return enValue;
        }
        return fallback;
      }
    }
    
    return value !== undefined ? value : fallback;
  } catch (error) {
    console.warn(`Error getting translation for ${namespace}.${key}:`, error);
    return fallback;
  }
};

/**
 * Hook to access translations in components
 * @returns {Object} Object with t function, current language, and namespace-specific helpers
 */
 export const useTranslations = () => {
  const { currentLang } = useContext(LanguageContext);

  /**
   * Get translation for a specific namespace and key
   * @param {string} namespace - Translation namespace (e.g., 'home', 'common')
   * @param {string} key - Translation key (can be nested with dots, e.g., 'hero.title')
   * @param {any} fallback - Fallback value if translation not found
   * @returns {any} Translated value or fallback
   */
  const t = (namespace, key, fallback = key) => {
    return getTranslation(currentLang, namespace, key, fallback);
  };

  /**
   * Get a namespace-specific translation function
   * Useful when a component primarily uses translations from one namespace
   * @param {string} namespace - The default namespace to use
   * @returns {Object} Object with t function scoped to the namespace
   */
  const withNamespace = (namespace) => {
    return {
      /**
       * Get translation from the pre-set namespace
       * @param {string} key - Translation key
       * @param {any} fallback - Fallback value if translation not found
       * @returns {any} Translated value or fallback
       */
      t: (key, fallback = key) => getTranslation(currentLang, namespace, key, fallback),
      currentLang,
    };
  };

  return {
    t,
    currentLang,
    withNamespace,
  };
};


/**
 * Get translation for current language (used outside of React components)
 * @param {string} namespace - Translation namespace
 * @param {string} key - Translation key  
 * @param {string} lang - Language code
 * @returns {any} Translated value
 */
export const translate = (namespace, key, lang = 'en') => {
  return getTranslation(lang, namespace, key, key);
};

/**
 * Get all translations for a namespace and language
 * @param {string} namespace - Translation namespace
 * @param {string} lang - Language code
 * @returns {Object} All translations for the namespace and language
 */
export const getAllTranslations = (namespace, lang = 'en') => {
  const translations = TRANSLATIONS[namespace];
  return translations?.[lang] || translations?.en || {};
};

// Export all translation namespaces for direct import if needed
export {
  commonTranslations,
  headerTranslations,
  footerTranslations,
  homeTranslations,
  aboutTranslations,
  contactTranslations,
  serviceTranslations,
  labTranslations,
  principlesTranslations,
  privacyTranslations,
  disclaimerTranslations,
  accessibilityTranslations,
  galleryTranslations,
  acrossChaptersMapTranslations,
};